import { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword } from "firebase/auth";
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "./firebase";

const auth = getAuth();

export default function LoyaltyClaimPage() {
  const [state, setState]         = useState("loading");
  const [user, setUser]           = useState(null);
  const [contact, setContact]     = useState(null);
  const [errorMsg, setErrorMsg]   = useState("");
  const [walletUrls, setWalletUrls] = useState({ apple: null, google: null });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) { setState("sign-in"); return; }
      setUser(firebaseUser);
      await loadContact(firebaseUser);
    });
    return unsub;
  }, []);

  async function loadContact(firebaseUser) {
    setState("loading");
    try {
      const q        = query(collection(db, "contact"), where("email", "==", firebaseUser.email));
      const snapshot = await getDocs(q);
      if (snapshot.empty) { setState("not-a-member"); return; }
      const docSnap     = snapshot.docs[0];
      const contactData = { _docId: docSnap.id, ...docSnap.data() };
      if (contactData.Loyalty && typeof contactData.Loyalty === "string") {
        setState("already-claimed");
        return;
      }
      setContact(contactData);
      setState("confirm");
    } catch (err) {
      console.error(err);
      setErrorMsg("Erreur lors du chargement. Réessayez.");
      setState("error");
    }
  }

  async function handleSignIn(email, password) {
    setState("loading");
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      const msg =
        err.code === "auth/invalid-credential" ||
        err.code === "auth/user-not-found"     ||
        err.code === "auth/wrong-password"
          ? "E-mail ou mot de passe incorrect."
          : err.code === "auth/too-many-requests"
          ? "Trop de tentatives. Attendez quelques minutes."
          : "Connexion échouée. Réessayez.";
      setErrorMsg(msg);
      setState("sign-in-error");
    }
  }

  function formatPhone(phone) {
    const digits = phone.replace(/\D/g, "");
    if (digits.startsWith("216") && digits.length === 11) return `+${digits}`;
    if (digits.startsWith("00216")) return `+${digits.slice(2)}`;
    if (digits.length === 8) return `+216${digits}`;
    return `+${digits}`;
  }

  async function handleClaim() {
    setState("submitting");
    try {
      const res = await fetch("https://automate.westudy.tn/webhook/claim-loyalty-card", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: contact.Name     || "",
          last_name:  contact.Lastname || "",
          email:      contact.email,
          phone:      contact.Phone ? formatPhone(String(contact.Phone)) : undefined,
        }),
      });
      const text = await res.text();
      if (!res.ok) {
        const err = text ? JSON.parse(text) : {};
        throw new Error(err?.message || `HTTP ${res.status}`);
      }
      const data = text ? JSON.parse(text) : {};

      // Save to Firestore
      await updateDoc(doc(db, "contact", contact._docId), {
        Loyalty:     String(data.customer_id || ""),
        LoyaltyUUID: data.card_uuid || "",
      });

      // Store wallet URLs and show wallet screen
      setWalletUrls({
        apple:  data.apple_wallet_url  || null,
        google: data.google_wallet_url || null,
      });
      setState("wallet");
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || "Une erreur est survenue. Réessayez.");
      setState("error");
    }
  }

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        <Logo />
        {state === "loading"         && <LoadingView />}
        {state === "sign-in"         && <SignInView onSignIn={handleSignIn} errorMsg="" />}
        {state === "sign-in-error"   && <SignInView onSignIn={handleSignIn} errorMsg={errorMsg} />}
        {state === "not-a-member"    && <NotAMemberView />}
        {state === "already-claimed" && <AlreadyClaimedView />}
        {state === "confirm"         && <ConfirmView contact={contact} onClaim={handleClaim} />}
        {state === "submitting"      && <SubmittingView />}
        {state === "wallet"          && <WalletView apple={walletUrls.apple} google={walletUrls.google} />}
        {state === "error"           && <ErrorView message={errorMsg} onRetry={() => loadContact(user)} />}
      </div>
    </div>
  );
}

function Logo() {
  return (
    <div style={styles.logoRow}>
      <div style={styles.logoMark}>W</div>
      <span style={styles.logoText}>WeStudy</span>
    </div>
  );
}

function LoadingView() {
  return (
    <div style={styles.center}>
      <div style={styles.spinner} />
      <p style={styles.muted}>Chargement…</p>
    </div>
  );
}

function SignInView({ onSignIn, errorMsg }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  return (
    <div>
      <div style={styles.pillBadge}>Carte de fidélité WeStudy</div>
      <h2 style={styles.heading}>Connectez-vous pour récupérer votre carte</h2>
      <p style={styles.body}>Utilisez vos identifiants WeStudy habituels.</p>
      <div style={styles.field}>
        <label style={styles.label}>Adresse e-mail</label>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="vous@exemple.com" style={styles.input} />
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Mot de passe</label>
        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
          placeholder="••••••••" style={styles.input}
          onKeyDown={e => e.key === "Enter" && onSignIn(email, password)} />
      </div>
      {errorMsg && <p style={styles.errorMsg}>{errorMsg}</p>}
      <button style={styles.btn} onClick={() => onSignIn(email, password)} disabled={!email || !password}>
        Se connecter →
      </button>
      <p style={styles.muted}>
        Pas encore membre ?{" "}
        <a href="mailto:contact@westudy.tn" style={styles.link}>Contactez-nous</a>
      </p>
    </div>
  );
}

function NotAMemberView() {
  return (
    <div style={styles.center}>
      <div style={{ ...styles.iconCircle, background: "#FFF3E0" }}>
        <span style={{ fontSize: 28 }}>🔒</span>
      </div>
      <h2 style={styles.heading}>Compte WeStudy introuvable</h2>
      <p style={styles.body}>Cette carte est réservée aux membres existants.</p>
      <p style={styles.muted}>
        <a href="mailto:contact@westudy.tn" style={styles.link}>Contactez-nous</a>
      </p>
    </div>
  );
}

function AlreadyClaimedView() {
  return (
    <div style={styles.center}>
      <div style={{ ...styles.iconCircle, background: "#E8F5E9" }}>
        <span style={{ fontSize: 28 }}>✅</span>
      </div>
      <h2 style={styles.heading}>Carte déjà récupérée</h2>
      <p style={styles.body}>Vérifiez votre boîte e-mail pour retrouver votre lien de carte.</p>
      <p style={styles.muted}>Besoin d'aide ? contact@westudy.tn</p>
    </div>
  );
}

function ConfirmView({ contact, onClaim }) {
  return (
    <div>
      <div style={styles.pillBadge}>Votre carte de fidélité</div>
      <h2 style={styles.heading}>Récupérez votre carte gratuite</h2>
      <p style={styles.body}>
        Collectez des tampons à chaque achat d'heures de coworking et débloquez
        des récompenses exclusives.
      </p>
      <div style={styles.detailsBox}>
        <DetailRow label="Nom"      value={`${contact?.Name || ""} ${contact?.Lastname || ""}`.trim()} />
        <DetailRow label="E-mail"   value={contact?.email} />
        {contact?.Phone && <DetailRow label="Téléphone" value={String(contact.Phone)} />}
      </div>
      <p style={styles.hint}>
        Votre carte sera générée directement — ajoutez-la à Apple Wallet ou Google Wallet en un tap.
      </p>
      <button style={styles.btn} onClick={onClaim}>
        Générer ma carte →
      </button>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={styles.detailRow}>
      <span style={styles.detailLabel}>{label}</span>
      <span style={styles.detailValue}>{value}</span>
    </div>
  );
}

function SubmittingView() {
  return (
    <div style={styles.center}>
      <div style={styles.spinner} />
      <p style={styles.muted}>Génération de votre carte…</p>
    </div>
  );
}

function WalletView({ apple, google }) {
  return (
    <div style={styles.center}>
      <div style={{ ...styles.iconCircle, background: "#E8F5E9" }}>
        <span style={{ fontSize: 28 }}>🎉</span>
      </div>
      <h2 style={styles.heading}>Votre carte est prête !</h2>
      <p style={styles.body}>
        Appuyez sur le bouton correspondant à votre téléphone pour ajouter
        votre carte de fidélité WeStudy à votre wallet.
      </p>

      <div style={styles.walletButtons}>
        {apple && (
          <a href={apple} target="_blank" rel="noreferrer" style={styles.walletBtn}>
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/30/Add_to_Apple_Wallet_badge.svg/3840px-Add_to_Apple_Wallet_badge.svg.png"
              alt="Add to Apple Wallet"
              style={{ height: "48px", width: "auto", display: "block" }}
            />
          </a>
        )}
        {google && (
          <a href={google} target="_blank" rel="noreferrer" style={styles.walletBtn}>
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/bb/Add_to_Google_Wallet_badge.svg/1280px-Add_to_Google_Wallet_badge.svg.png"
              alt="Add to Google Wallet"
              style={{ height: "48px", width: "auto", display: "block" }}
            />
          </a>
        )}
      </div>

      <p style={styles.muted}>
        Besoin d'aide ? <a href="mailto:contact@westudy.tn" style={styles.link}>contact@westudy.tn</a>
      </p>
    </div>
  );
}

function ErrorView({ message, onRetry }) {
  return (
    <div style={styles.center}>
      <div style={{ ...styles.iconCircle, background: "#FFEBEE" }}>
        <span style={{ fontSize: 28 }}>⚠️</span>
      </div>
      <h2 style={styles.heading}>Une erreur est survenue</h2>
      <p style={styles.body}>{message}</p>
      <button style={{ ...styles.btn, background: "#555" }} onClick={onRetry}>Réessayer</button>
    </div>
  );
}

const styles = {
  root: {
    minHeight: "100vh", background: "#F5F4F0", display: "flex",
    alignItems: "center", justifyContent: "center", padding: "24px 16px",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  },
  card: {
    background: "#FFFFFF", borderRadius: "20px", padding: "40px 36px",
    maxWidth: "460px", width: "100%", boxShadow: "0 4px 40px rgba(0,0,0,0.08)",
  },
  logoRow: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "32px" },
  logoMark: {
    width: "36px", height: "36px", borderRadius: "10px", background: "#1A1A2E",
    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 700, fontSize: "18px",
  },
  logoText: { fontSize: "18px", fontWeight: 600, color: "#1A1A2E", letterSpacing: "-0.3px" },
  heading: { fontSize: "22px", fontWeight: 700, color: "#1A1A2E", margin: "0 0 12px", lineHeight: 1.2, letterSpacing: "-0.4px" },
  body: { fontSize: "15px", color: "#555", lineHeight: 1.6, margin: "0 0 20px" },
  muted: { fontSize: "13px", color: "#999", margin: "16px 0 0" },
  link: { color: "#1A1A2E", fontWeight: 600, textDecoration: "underline" },
  center: { textAlign: "center", padding: "8px 0" },
  iconCircle: {
    width: "64px", height: "64px", borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px",
  },
  pillBadge: {
    display: "inline-block", background: "#F0F0FF", color: "#4444BB",
    fontSize: "12px", fontWeight: 600, padding: "4px 12px",
    borderRadius: "100px", marginBottom: "16px", letterSpacing: "0.3px",
  },
  field: { marginBottom: "16px" },
  label: { display: "block", fontSize: "13px", fontWeight: 600, color: "#444", marginBottom: "6px" },
  input: {
    width: "100%", padding: "12px 14px", border: "1.5px solid #E0E0E0",
    borderRadius: "10px", fontSize: "15px", color: "#1A1A2E", outline: "none",
    boxSizing: "border-box", background: "#FAFAFA",
  },
  errorMsg: {
    fontSize: "13px", color: "#C0392B", background: "#FFEBEE",
    border: "1px solid #FFCDD2", borderRadius: "8px", padding: "10px 14px", margin: "0 0 16px",
  },
  detailsBox: {
    background: "#F8F8F6", borderRadius: "12px", padding: "4px 0",
    margin: "20px 0", border: "1px solid #EBEBEB",
  },
  detailRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "12px 16px", borderBottom: "1px solid #EBEBEB",
  },
  detailLabel: { fontSize: "13px", color: "#999", fontWeight: 500 },
  detailValue: { fontSize: "14px", color: "#1A1A2E", fontWeight: 600 },
  hint: {
    fontSize: "13px", color: "#888", lineHeight: 1.5, margin: "0 0 24px",
    padding: "12px 14px", background: "#FFFBF0", borderRadius: "8px", border: "1px solid #F5E6C0",
  },
  btn: {
    width: "100%", padding: "14px", background: "#1A1A2E", color: "#fff",
    border: "none", borderRadius: "12px", fontSize: "15px", fontWeight: 600,
    cursor: "pointer", letterSpacing: "-0.2px",
  },
  walletButtons: {
    display: "flex", flexDirection: "column", alignItems: "center",
    gap: "12px", margin: "24px 0",
  },
  walletBtn: { display: "block", textDecoration: "none" },
  spinner: {
    width: "32px", height: "32px", border: "3px solid #EBEBEB",
    borderTop: "3px solid #1A1A2E", borderRadius: "50%",
    animation: "spin 0.8s linear infinite", margin: "0 auto 16px",
  },
};

const styleTag = document.createElement("style");
styleTag.innerHTML = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleTag);